import React from 'react';
import { Form, Formik } from 'formik';
import ReactQuill from 'react-quill';
import { Download } from 'feather-icons-react';
import { Edit } from 'feather-icons-react/build/IconComponents';
import { TemplateButton } from 'components/TemplateButton';
import DateUtils from '../../../../helpers/dateUtils';

import '../../../../css/quillCustom.css';

const TicketMessages = ({
  ticketData,
  handleSubmit,
  sending,
  selectedFiles,
  setSelectedFiles,
  setModal
}) => {
  const dateUtils = new DateUtils();

  if (!ticketData.messages) {
    return null;
  }

  return (
    <div className="w-full md:w-1/2">
      <div className="w-full bg-white flex items-center justify-center rounded-[12px]">
        <div className="w-full mt-[20px] mb-[20px]">
          <div className="max-h-[800px] flex flex-col">
            <div className="w-full flex justify-between items-center mb-4 pb-2 px-4 border-b border-slate-300">
              <div>
                <p className="font-bold">
                  {ticketData.agencyName}
                </p>
                <p className="text-sm">
                  {ticketData.assignedAgentEmail}
                </p>
                <p className="text-sm">
                  {dateUtils.timestamp(ticketData.updatedAt)}
                </p>
              </div>

              {ticketData.ticketStatus === 'Completed' ? null : (
                <button
                  className="bg-[#d32425] text-white px-4 py-2 rounded-md disabled:bg-gray-200 disabled:cursor-not-allowed"
                  onClick={() => {
                    setModal({
                      name: 'complete_ticket_modal',
                      data: {
                        id: ticketData.id,
                        status: ticketData.status,
                        ticketStatus: ticketData.ticketStatus,
                      },
                    });
                  }}
                  disabled={
                    ticketData.ticketStatus === 'Completed'
                  }
                >
                  Mark as Complete
                </button>
              )}
            </div>

            {/* Ticket messages */}
            <div className="flex-1 overflow-y-scroll px-4 py-2 flex flex-col-reverse">
              {ticketData.messages
                .slice()
                .sort(
                  (a, b) =>
                    new Date(b.sendAt) - new Date(a.sendAt)
                )
                .map((message, index) => (
                  <div
                    key={index}
                    className="bg-gray-100 rounded-md p-2 mb-2 w-full"
                  >
                    <div id="title" className="mb-2 p-1">
                      Subject: {message.title}
                    </div>
                    <div
                      className="whitespace-pre-wrap p-1"
                      dangerouslySetInnerHTML={{ __html: message.description }}
                    />
                    <br />
                    <div className="w-full flex justify-between items-center p-1">
                      <div>{message.sender}</div>
                      <div>
                        {dateUtils.timestamp(message.sendAt)}
                      </div>
                    </div>
                    {message.documents.length > 0 && (
                      <div className="mt-2 mb-2">
                        <ul className="flex flex-wrap">
                          {message.documents.map(
                            (document, index) => {
                              const urlParts =
                                document.split('/');
                              const fileNameWithTimestamp =
                                urlParts[urlParts.length - 1];
                              const fileName =
                                fileNameWithTimestamp.split(
                                  '-'
                                )[0];
                              const decodedFileName =
                                decodeURIComponent(fileName);
                              const truncatedDocument =
                                decodedFileName.length > 18
                                  ? decodedFileName.substring(
                                    0,
                                    18
                                  ) + '...'
                                  : decodedFileName;

                              return (
                                <label
                                  key={index}
                                  htmlFor="custom-input"
                                  className="
                                  text-md text-white mt-2 mr-2 py-2
                                  px-4 rounded-md border-0 bg-[#d32425]
                                  cursor-pointer max-w-[180px] flex
                                  justify-center items-center
                                "
                                  title="click to download file"
                                >
                                  <a
                                    href={document}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="
                                    text-white no-underline truncate
                                    flex items-center whitespace-nowrap
                                    overflow-hidden text-ellipsis
                                  "
                                  >
                                    <span
                                      style={{
                                        marginRight: '8px',
                                      }}
                                    >
                                      {truncatedDocument}
                                    </span>
                                    <Download
                                      className="text-white"
                                      size={16}
                                    />
                                  </a>
                                </label>
                              );
                            }
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
            </div>

            {ticketData.ticketStatus === 'Completed' ? null : (
              <Formik
                initialValues={{
                  title: '',
                  description: '',
                  documents: [],
                }}
                onSubmit={handleSubmit}
              >
                {({
                  setFieldValue,
                  handleChange,
                  values,
                  setFieldTouched
                }) => (
                  <Form encType="multipart/form-data">
                    <div className="w-full mb-4 px-4 py-2">
                      <div className="flex justify-between items-center mb-2">
                        <input
                          type="text"
                          className="flex-1 p-2 border border-slate-300 rounded-lg mr-2"
                          placeholder="Subject..."
                          name="title"
                          value={values.title}
                          onChange={handleChange}
                        />
                        <TemplateButton 
                          toolType="service_center"
                          onTemplateSelect={(template) => {
                            setFieldValue('title', template.title);
                            // Convert newlines to HTML breaks for ReactQuill
                            const formattedMessage = template.message.replace(/\n/g, '<br>');
                            setFieldValue('description', formattedMessage);
                          }}
                        />
                      </div>

                      <ReactQuill
                        value={values.description}
                        onChange={(content) => {
                          setFieldValue('description', content);
                          setFieldTouched('description', true, false);
                        }}
                        modules={{
                          toolbar: [
                            ['bold', 'italic', 'underline', 'strike'],
                            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                            ['clean']
                          ],
                        }}
                        formats={[
                          'bold', 'italic', 'underline', 'strike',
                          'list', 'bullet'
                        ]}
                        placeholder="Write a message"
                      />

                      <ul className="flex flex-wrap">
                        {Array.from(selectedFiles).map(
                          (file) => (
                            <label
                              key={file.name}
                              htmlFor="custom-input"
                              className="
                            block text-md text-white mt-2 mr-2 py-2 px-4
                            rounded-md border-0 bg-[#d32425]
                            cursor-pointer w-[200px]
                            "
                              title="Click to change file"
                            >
                              <div className="flex items-center">
                                <span
                                  style={{
                                    marginRight: '8px',
                                  }}
                                >
                                  {file.name.length > 18
                                    ? file.name.substring(
                                      0,
                                      18
                                    ) + '...'
                                    : file.name}
                                </span>
                                <Edit
                                  className="text-white"
                                  size={16}
                                />
                              </div>
                            </label>
                          )
                        )}
                      </ul>
                    </div>

                    <div className="w-full flex justify-between px-4 py-2">
                      <div className="flex flex-row items-center gap-4">
                        <input
                          type="file"
                          id="custom-input"
                          onChange={(event) => {
                            setSelectedFiles(
                              event.target.files
                            );
                          }}
                          hidden
                          multiple
                          disabled={
                            ticketData.ticketStatus ===
                            'Completed'
                          }
                        />
                        <label
                          htmlFor="custom-input"
                          className="
                            block text-md text-white py-2
                            px-4 rounded-md border-0 bg-[#d32425]
                            cursor-pointer disabled:cursor-not-allowed
                            "
                          disabled={
                            ticketData.ticketStatus ===
                            'Completed'
                          }
                        >
                          Attach one or more documents
                        </label>
                      </div>

                      <button
                        className="
                          bg-[#d32425] text-white
                          px-4 py-2 rounded-md disabled:bg-gray-200
                          disabled:cursor-not-allowed
                        "
                        type="submit"
                        disabled={
                          sending ||
                          ticketData.ticketStatus ===
                          'Completed' ||
                          !values.title
                        }
                      >
                        {sending ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </Form>
                )}
              </Formik>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TicketMessages;
